import Icon from '../../Icon';
import OpportunityCard from '../OpportunityCard';
import Typography from '../../Typography';
import cn from '../../Utils/cn';
import { FC } from 'react';
import FilterCheckboxSection from './FilterCheckboxSection';
import Navigation from './Navigation';
import SelectField from '../../Form/SelectField';
import generateJobUrl  from '../../IncluiPCD/JobSearch/generateJobUrl';

type Props = {
	jobs: any;
	filterData?: any;
	workPlaceOptions: { value: string; label: string }[];
	educationOptions: { value: string; label: string }[];
	sectorOptions: { value: string; label: string }[];
	page: number;
	pageMax: number;
	filterOpen: boolean;
	setFilterOpen: (value: boolean) => void;
	sponsors: string[];
	supporters: string[];
	setCompanies: (value: string | string[]) => void;
	workPlaces: string[];
	setWorkPlaces: (value: string[]) => void;
	educations: string[];
	setEducations: (value: string[]) => void;
	sectors: string[];
	setSectors: (value: string[]) => void;
	onNextPage: () => void;
	onPreviousPage: () => void;
};

const BottomSection: FC<Props> = ({
	jobs,
	educationOptions,
	sectorOptions,
	workPlaceOptions,
	filterData,
	page,
	pageMax,
	filterOpen,
	setFilterOpen,
	educations,
	sectors,
	workPlaces,
	setEducations,
	setSectors,
	setWorkPlaces,
	sponsors,
	supporters,
	setCompanies,
	onNextPage,
	onPreviousPage,
}) => {
	// Patrocinadores e apoiadores são unificados em um único filtro de empresas.
	const companyOptions = Array.from(
		new Map(
			[...(filterData?.sponsors ?? []), ...(filterData?.supporters ?? [])].map((company: any) => [
				company.id,
				{ value: company.id, label: company.name },
			]),
		).values(),
	);
	const selectedCompanies = Array.from(new Set([...(sponsors ?? []), ...(supporters ?? [])]));

	return (
		<div className="container pt-0 pb-0 rounded-t-4xl md:rounded-none lg:pb-0 bg-base-grey-3 md:bg-transparent" id="results">
			<div className="relative flex w-full">
				<div className="absolute bg-white top-0 bottom-0 -left-full w-full hidden md:block" aria-hidden></div>
				<div
					className={cn('fixed md:static inset-0 md:w-1/3 lg:w-1/4 pt-64 md:py-32 md:pl-0 bg-black/60 md:bg-white md:block', {
						hidden: !filterOpen,
					})}
					onClick={() => {
						setFilterOpen(false);
					}}
				>
					<div className="h-full w-full rounded-t-4xl bg-white relative" onClick={e => e.stopPropagation()}>
						<button className="text-5xl text-black/50 absolute top-10 right-6 bg-white rounded-full md:hidden" onClick={() => setFilterOpen(false)} aria-label="Fechar filtros">
							<Icon name="close" />
						</button>

						<div className="h-full w-full px-8 py-8 md:p-0 md:pr-8 overflow-auto">
							<Typography variant="headline-4" className="mb-16 text-base-grey-5">
								Filtros
							</Typography>

							<SelectField
								label="Empresas"
								labelProps={{
									className: 'text-base-grey-5 text-lg font-bold mb-16 block',
								}}
								showPills={true}
								value={selectedCompanies}
								initialValue={selectedCompanies}
								setValue={setCompanies}
								className="mb-16"
								options={companyOptions}
								multiple={true}
							/>

							<FilterCheckboxSection title="Local de trabalho" options={workPlaceOptions} selected={workPlaces} setSelected={setWorkPlaces} />

							<FilterCheckboxSection title="Escolaridade" options={educationOptions} selected={educations} setSelected={setEducations} />

							<FilterCheckboxSection title="Setor" options={sectorOptions} selected={sectors} setSelected={setSectors} />
						</div>
					</div>
				</div>

				<div className="absolute bg-base-grey-3 top-0 bottom-0 -right-full w-full hidden md:block"></div>
				<div className="w-full bg-base-grey-3 pb-32 pt-16 lg:pt-32 lg:pb-48 md:pl-16 lg:pl-32 gap-16 flex flex-col items-center">
					{jobs?.data?.length > 0 && (
						<>
							<Typography variant="body-1" lg="body-1" className="text-base-grey-4 md:ml-auto mb-8">
								Mostrando {(page - 1) * jobs?.pageSize + jobs?.data?.length} de {jobs?.count} vagas
							</Typography>

							{jobs.data.map((job: any) => {
								return (
									<OpportunityCard
										key={job.code}
										className="w-full lg:w-full xl:w-full md:p-8 lg:p-8 xl:p-8"
										sector={job.sector}
										city={job.remoteWorkPolicy === 'Trabalho Remoto Permanente' ? 'Trabalho Remoto' : `${job.address?.city}/${job.address?.state}`}
										companyName={job.contact?.name?.full}
										cod={job.code}
										edRequirement={job.education}
										edStatus={job.educationStatus}
										jobTitle={job.name}
										salary={job.wageMax?.value}
										href={generateJobUrl(job)}
										companyLogo={job.companyLogo}
										variant="search"
									/>
								);
							})}

							<Navigation page={page} pageMax={pageMax} onPrevious={onPreviousPage} onNext={onNextPage} />
						</>
					)}

					{!jobs?.data?.length && (
						<Typography variant="body-2" lg="body-1" className="text-base-grey-4 mx-auto mb-32">
							Nenhuma vaga encontrada
						</Typography>
					)}
				</div>
			</div>
		</div>
	);
};

export default BottomSection;